import { formatBalance } from '@polkadot/util';
import { saveAs } from 'file-saver';

export function arrayOfSize(size: number): number[] {
  return new Array(size).fill(null).map((_, i) => i);
}

export function truncate(value: string | undefined, sideLength = 6): string {
  return value
    ? value.length > sideLength * 2
      ? `${value.substring(0, sideLength)}...${value.substring(value.length - sideLength)}`
      : value
    : '';
}

export function formatAmount(amount = '0', decimalPoint = 2) {
  const _amount = Number(amount.replaceAll(',', ''));
  return `${_amount ? (_amount / Math.pow(10, 12)).toFixed(decimalPoint) : 0} ANLOG`;
}

export function newFormatBalance(value: string) {
  const amount = value.replaceAll(',', '');
  return amount.length > 15
    ? formatBalance(amount, formatBalanceOption)
    : formatBalance(amount, formatBalanceOptionRaw);
}

export const formatBalanceOption = {
  decimals: 12,
  withSi: true,
  withUnit: false,
};

export const formatBalanceOptionHighest = {
  withSi: false,
  withUnit: false,
  forceUnit: 'dot',
  withZero: false,
};

export const formatBalanceOptionRaw = {
  withSi: false,
  decimals: 12,
  forceUnit: 'dot',
};

export const isJSONString = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const SortColumnsBlock = [{ Block: 'number' }, { Status: 'status' }, { Age: 'created_at' }];

export const SortColumnsExtrinsics = [
  { 'Extrinsic ID': 'id' },
  { 'Block Number': 'block_number' },
  { Age: 'created_at' },
  { Action: 'section' },
];

export const SortColumnsMembers = [
  { 'Account ID': 'account_id' },
  { Network: 'network' },
  { State: 'state' },
  { 'Shard ID': 'shardId' },
];

export const SortColumnsGmp = [
  { 'Account ID': 'account_id' },
  { Network: 'network' },
  { State: 'state' },
  { 'Shard ID': 'shardId' },
];

export const SortColumnsValidators = [
  { 'Self Bonded': 'selfBonded' },
  { 'Total Bonded': 'totalBonded' },
  { 'Reward Points': 'eraRewardPoints' },
];

export const SortColumnsTask = [
  { Phase: 'phase' },
  { Type: 'type' },
  { Network: 'network' },
  { 'Shard ID': 'shardId' },
];

export const SortColumnsShard = [{ Network: 'network' }, { State: 'state' }, { 'Shard ID': 'id' }];

export const SortColumnsAccount = [{ Balance: 'balance_amount' }];

export const parseJson = (json?: string | null | undefined) => {
  try {
    return JSON.parse(json || '[]');
  } catch {
    return [json];
  }
};

export const getLinkFromNetwork = (
  path: string | undefined | null,
  network: string | undefined | null
) => {
  if (!path || !network) return undefined;
  const chain = network;
  switch (chain.toLowerCase()) {
    case 'ethereum':
      return `https://eth-sepolia.blockscout.com/${path}`;
    case 'astar':
      return `https://shibuya.blockscout.com/${path}`;
    case 'polygon':
      return `https://amoy.polygonscan.com/${path}`;
    case 'binance':
      return `https://testnet.bscscan.com/${path}`;
    case 'arbitrum':
      return `https://sepolia.arbiscan.io/${path}`;
    default:
      return undefined;
  }
};

export const parseStringToArray = (sigString: any) => {
  let result = [];

  if (typeof sigString === 'string') {
    if (sigString.startsWith('[') && sigString.endsWith(']')) {
      const items = sigString.slice(1, -1).split(',');

      result = items.map((item) => item.trim().replace(/['"]+/g, ''));
    } else {
      result.push(sigString.replace(/['"]+/g, ''));
    }
  } else if (Array.isArray(sigString)) {
    result = sigString.map((item) => item.toString());
  }

  return result;
};

export function extractFirstValue(data: any) {
  if (Array.isArray(data)) {
    return data[0];
  }

  if (typeof data === 'string') {
    data = data.trim();

    if (data.startsWith('[') && data.endsWith(']')) {
      return data
        .slice(1, -1)
        .split(',')
        .map((item: string) => item.trim())[0];
    }
    return data.split(',').map((item: string) => item.trim())[0];
  }
  return '';
}

export type colorType = 'blue' | 'yellow' | 'green' | 'purple' | 'red' | 'brightGreen';

export const colors = {
  message_received: 'purple',
  message_executed: 'yellow',
  completed: 'green',
  created: 'blue',
  processing: 'purple',
};

export const DBtoStringValueMapping = {
  message_received: 'MESSAGE_RECEIVED',
  message_executed: 'MESSAGE_EXECUTED',
  completed: 'COMPLETED',
  processing: 'PROCESSING',
  created: 'CREATED',
};

export const getNetworkName = (networkStr: string | undefined): string => {
  if (!networkStr) return '';

  if (networkStr.startsWith('@')) {
    return networkStr.replace('@', '').split(' ')[0];
  }
  if (networkStr.startsWith('<')) {
    return networkStr.replace('<', '').split(' ')[0];
  }
  if (/^\d/.test(networkStr)) {
    return networkStr.substring(1).split(' ')[0];
  }

  return networkStr;
};

export const parseData = (value?: string | string[], seperator = '-') => {
  if (!value) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.join(seperator);
  }
  if (value.startsWith('[')) {
    return value
      .replaceAll('"', '')
      .replaceAll('[', '')
      .replaceAll(']', '')
      .replaceAll('', '')
      .replaceAll(',', seperator);
  }
  return value;
};

export function downloadCSV(
  fileName: string,
  headers: string[],
  data: (string | number | null | undefined)[][]
) {
  if (!data || data.length === 0) {
    return;
  }

  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      row.map((value) => (value !== null && value !== undefined ? value : '')).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, fileName);
}

export const parseNetwork = (networkString: string | undefined): string => {
  if (!networkString) return '';
  const cleanString = networkString.length > 1 ? networkString.substring(1) : networkString;
  const parts = cleanString.split(' ').filter((part) => part.length > 0);
  return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('-');
};

export const removeZeros = (address: string | undefined): string => {
  if (!address || !address.startsWith('0x')) return address || '';
  return '0x' + address.substring(2).replace(/^0+/, '');
};

export function hexToString(hex: string) {
  if (hex.startsWith('0x')) {
    hex = hex.slice(2);
  }

  const prefixLength = 4;
  hex = hex.slice(prefixLength);

  let str = '';
  for (let i = 0; i < hex.length; i += 2) {
    const code = parseInt(hex.substr(i, 2), 16);
    if (!isNaN(code)) {
      str += String.fromCharCode(code);
    }
  }
  return str;
}

export const safeFormatBalance = (amount: any): string => {
  try {
    if (!amount) return '0 ANLOG';
    const cleanAmount = String(amount).replace(/,/g, '');
    return `${formatBalance(cleanAmount, formatBalanceOptionRaw)} ANLOG`;
  } catch (error) {
    console.error('Error formatting balance:', error);
    return `${amount || '0'} ANLOG`;
  }
};
